import classNames from "classnames";
import React, { Component, createRef } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Form from "Shared/Form";
import PhoneInput from "Shared/Form/Input/PhoneInput";
import { email, phone, required } from "Shared/Form/validators";
import styled, { css } from "styled-components";
import StepContent from "../../StepContent/StepContent";
import { mapFieldsToValues } from "../shared/fieldValueMapper";

const TRANSLATION_KEY = "ContinueDeal.CreditApp.Contact";

const ColumnStyle = css`
    display: inline-block;
    padding: 10px;
    vertical-align: top;
`;
const ColumnFull = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);
`;
const ColumnHalf = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: calc(50% - 20px);
    }
`;
const ColumnMedium = styled("div")`
    ${ColumnStyle};
    width: calc(100% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: calc((100% - 60.1px) / 7 * 3);
    }
`;
const ColumnMediumLarge = styled("div")`
    ${ColumnStyle};
    width: calc(70% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: calc((100% - 60.1px) / 7 * 3);
    }
`;
const ColumnSmall = styled("div")`
    ${ColumnStyle};
    width: calc(30% - 20px);

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: calc((100% - 60.1px) / 7);
    }
`;
const Title = styled("h3")`
    color: #333;
    border-bottom: 2px solid #e5e5e5;
    font-size: 1.5625em;
    font-weight: 400;
    padding: 0 0 15px;
    margin: 15px 0 0;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        font-size: 1.875em;
        padding: 0 0 8px;
    }
`;

const StyledForm = styled(Form)`
    margin: 20px 0;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 30px 0;
    }
`;

export interface ContactProps {
    className?: string;
    isCosigner?: boolean;
    onBackClick: (data: any) => void;
    onNextClick: (data: any) => void;
    formData?: any;
}

class Contact extends Component<ContactProps & WithTranslation> {
    private fieldsPerRow = [
        ["firstName", "middleInitial", "lastName"],
        ["emailAddress"],
        ["primaryPhone", "secondaryPhone"],
    ];

    private contactForm = createRef<Form>();

    public render() {
        const { className, isCosigner = false, formData = {}, t } = this.props;
        const fields = mapFieldsToValues(this.getFields(), formData);
        return (
            <div className={classNames("Contact", className)}>
                <StepContent onBackClick={this.handleBackClick} onNextClick={this.handleNextClick}>
                    <Title>
                        {isCosigner ? t(`${TRANSLATION_KEY}.cosignerTitle`) : t(`${TRANSLATION_KEY}.defaultTitle`)}
                    </Title>
                    <StyledForm fields={fields} fieldsPerRow={this.fieldsPerRow} ref={this.contactForm} />
                </StepContent>
            </div>
        );
    }

    private getFields() {
        const { t } = this.props;

        return {
            emailAddress: {
                label: `${t(`${TRANSLATION_KEY}.emailLabel`)}*`,
                validators: [{ message: t(`${TRANSLATION_KEY}.emailRequiredMessage`), validator: required }, email],
                wrapper: ColumnFull,
            },
            firstName: {
                label: `${t(`${TRANSLATION_KEY}.firstNameLabel`)}*`,
                validators: [
                    {
                        message: t(`${TRANSLATION_KEY}.firstNameRequiredMessage`),
                        validator: required,
                    },
                ],
                wrapper: ColumnMediumLarge,
            },
            lastName: {
                label: `${t(`${TRANSLATION_KEY}.lastNameLabel`)}*`,
                validators: [
                    {
                        message: t(`${TRANSLATION_KEY}.lastNameRequiredMessage`),
                        validator: required,
                    },
                ],
                wrapper: ColumnMedium,
            },
            middleInitial: {
                label: t(`${TRANSLATION_KEY}.middleInitialLabel`),
                validators: [],
                wrapper: ColumnSmall,
            },
            primaryPhone: {
                input: PhoneInput,
                label: `${t(`${TRANSLATION_KEY}.primaryPhoneLabel`)}*`,
                validators: [required, phone],
                wrapper: ColumnHalf,
            },
            secondaryPhone: {
                input: PhoneInput,
                label: t(`${TRANSLATION_KEY}.secondaryPhoneLabel`),
                validators: [phone],
                wrapper: ColumnHalf,
            },
        };
    }

    private handleNextClick = () => {
        if (this.contactForm.current!.isFormValid()) {
            const formData = this.contactForm.current!.getFormData();
            return this.props.onNextClick(formData);
        }
    };

    private handleBackClick = () => {
        const formData = this.contactForm.current!.getFormData();
        return this.props.onBackClick(formData);
    };
}

export default withTranslation()(Contact);
